/* Independent UI: never reads or changes product forms or variant state. */
if (!customElements.get('mammyhug-size-guide')) {
  customElements.define('mammyhug-size-guide', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      // A section can be parsed after this custom element has already been registered.
      if (!this.querySelector('dialog')) {
        requestAnimationFrame(() => { if (this.isConnected) this.connectedCallback(); });
        return;
      }
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.dialog = this.querySelector('dialog');
      this.trigger = this.querySelector('[data-sg-open]');
      this.tabs = [...this.querySelectorAll('[role="tab"]')];
      this.unit = 'cm';
      this.international = false;
      this.trigger.hidden = false;
      this.addEventListener('click', event => {
        const button = event.target.closest('button');
        if (!button || !this.contains(button)) return;
        if (button.hasAttribute('data-sg-open')) this.open();
        if (button.hasAttribute('data-sg-close')) this.dialog.close();
        if (button.hasAttribute('data-sg-type')) this.selectTab(button);
        if (button.hasAttribute('data-sg-unit')) {
          this.unit = button.dataset.sgUnit;
          this.update();
        }
        if (button.hasAttribute('data-sg-international')) {
          this.international = !this.international;
          this.update();
        }
      }, options);
      this.addEventListener('keydown', event => {
        if (event.key === 'Tab' && this.dialog.open) {
          const focusable = [...this.dialog.querySelectorAll('button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])')]
            .filter(node => node.tabIndex >= 0 && node.getClientRects().length > 0);
          const first = focusable[0];
          const last = focusable[focusable.length - 1];
          if (event.shiftKey && (document.activeElement === first || !this.dialog.contains(document.activeElement))) {
            event.preventDefault();
            last?.focus();
          } else if (!event.shiftKey && (document.activeElement === last || !this.dialog.contains(document.activeElement))) {
            event.preventDefault();
            first?.focus();
          }
          return;
        }
        if (event.target.getAttribute('role') !== 'tab') return;
        let index = this.tabs.indexOf(event.target);
        if (event.key === 'ArrowRight') index = (index + 1) % this.tabs.length;
        else if (event.key === 'ArrowLeft') index = (index - 1 + this.tabs.length) % this.tabs.length;
        else if (event.key === 'Home') index = 0;
        else if (event.key === 'End') index = this.tabs.length - 1;
        else return;
        event.preventDefault();
        this.selectTab(this.tabs[index]);
        this.tabs[index].focus();
      }, options);
      // Native showModal supplies focus containment, background inertness and Escape.
      this.dialog.addEventListener('click', event => {
        if (event.target !== this.dialog) return;
        const rect = this.dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.dialog.close();
      }, options);
      this.dialog.addEventListener('close', () => {
        this.trigger.setAttribute('aria-expanded', 'false');
        this.unlock();
        this.trigger.focus({ preventScroll: true });
      }, options);
      this.update();
    }

    disconnectedCallback() {
      if (this.dialog?.open) this.dialog.close();
      this.unlock();
      this.controller?.abort();
      this.controller = null;
    }

    open() {
      if (this.dialog.open) return;
      this.unit = 'cm';
      this.international = false;
      this.selectTab(this.tabs.find(tab => tab.dataset.sgType === this.dataset.initialType) || this.tabs[0]);
      this.previousOverflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';
      this.locked = true;
      this.dialog.showModal();
      this.trigger.setAttribute('aria-expanded', 'true');
      this.querySelector('[data-sg-close]').focus();
    }

    unlock() {
      if (!this.locked) return;
      document.documentElement.style.overflow = this.previousOverflow;
      this.locked = false;
    }

    selectTab(selected) {
      this.tabs.forEach(tab => {
        const active = tab === selected;
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
        this.querySelector(`#${CSS.escape(tab.getAttribute('aria-controls'))}`).hidden = !active;
      });
      this.update();
    }

    update() {
      this.querySelectorAll('[data-sg-unit]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.sgUnit === this.unit)));
      this.querySelector('[data-sg-units]').hidden = this.international;
      this.querySelector('[data-sg-international]').setAttribute('aria-pressed', String(this.international));
      this.querySelectorAll('[data-sg-measurements]').forEach(node => { node.hidden = this.international; });
      this.querySelectorAll('[data-sg-conversions]').forEach(node => { node.hidden = !this.international; });
      this.querySelectorAll('[data-sg-cm]').forEach(node => {
        const cm = node.dataset.sgCm;
        // Convert only a whole numeric value or range; preserve labels and missing data.
        const match = cm.trim().match(/^(\d+(?:[.,]\d+)?)\s*(?:[–—−-]\s*(\d+(?:[.,]\d+)?))?$/);
        node.textContent = this.unit === 'in' && match
          ? match.slice(1).filter(Boolean).map(value => String(Math.round(Number(value.replace(',', '.')) / 2.54 * 10) / 10)).join('–')
          : cm;
      });
      this.querySelectorAll('[data-sg-current-unit]').forEach(node => { node.textContent = this.unit; });
    }
  });
}
